import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import {
  Box, Container, Grid, Card, CardHeader, CardContent, Typography,
  Hidden, AlertColor,
  Divider,
  IconButton,
  Alert,
  Button,
  ListItemText,
  MenuItem,
  TextField,
  Toolbar,
  Stack,
  Chip
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import ErrorIcon from '@mui/icons-material/Error';
import CancelIcon from '@mui/icons-material/Cancel';
import ReportProblemIcon from '@mui/icons-material/ReportProblem';
import { connect } from 'react-redux';
import { hide, show } from '../store/actions/busyIndicatorActions';
import {
  showAlert as showGlobalAlert, clearAllAlerts as clearAllGlobalAlerts
} from '../store/actions/alertActions';
import {
  formatErrorMessage, formatDateToLongDate, getDaysFromNow
} from '../utils/common';
import AppConstants from '../constants/AppConstants';
import FleetIoService from '../services/FleetIoService';
import Table from '../components/Table';
import MobileDataList from '../components/MobileDataList';
import { EnumRes } from '../proxy/proxy';
import EnumService from '../services/EnumService';

interface Props {
  showAlert: (message: any) => any,
  clearAllAlerts: () => any,
  showBusyIndicator: () => any,
  hideBusyIndicator: () => any,
}

interface Warning {
  type: AlertColor;
  category: string;
  message: string;
}

interface ReminderType {
  id: number;
  account_id: number;
  name: string;
  created_at: string;
  updated_at: string;
}

interface CountsType {
  [key: string]: { breached: number; warning: number };
}

const RenewalReminders: React.FC<Props> = ({
  showAlert,
  clearAllAlerts,
  showBusyIndicator,
  hideBusyIndicator
}) => {
  const [rows, setRows] = useState([]);
  const [page, setPage] = useState(0);
  const [sortModel, setSortModel] = useState([]);
  const [warningsData, setWarningsData] = useState<Warning[]>([]);
  const [showAll, setShowAll] = useState(false);
  const [allStates, setAllStates] = useState<Array<EnumRes>>([]);
  const [selectedState, setSelectedState] = useState<number>(1);
  const [selectedSummary, setSelectedSummary] = useState<string>('');
  const [reminderTypes, setReminderTypes] = useState<ReminderType[]>([]);
  const [counts, setCounts] = useState<CountsType>({});
  const visibleWarnings = showAll ? warningsData : warningsData?.slice(0, 3);

  const columns = [
    { field: 'id', headerName: 'ID', width: 90 },
    { field: 'license_plate', headerName: 'Vehicle', width: 150 },
    { field: 'renewal_type', headerName: 'Renewal Type', flex: 1 },
    { field: 'next_due_at', headerName: 'Next Due', width: 180 },
    { field: 'due_soon_at', headerName: 'Due Soon', width: 180 },
    { field: 'status', headerName: 'Status', width: 120 },
  ];

  const renderEachMobileServiceCard = (row: any) => (
    <Card key={row.id} sx={{ marginBottom: '10px' }} variant="outlined">
      <CardHeader
        title={row.license_plate || 'No Plate Info'}
        subheader={`Status: ${row.status || 'N/A'}`}
      />
      <Divider />
      <CardContent>
        <Grid container spacing={1}>
          {row.renewal_type && (
            <Grid item xs={12}>
              <Typography variant="body2">
                <b>Task:</b> {row.renewal_type}
              </Typography>
            </Grid>
          )}
          {row.service_description && (
            <Grid item xs={12}>
              <Typography variant="body2">
                <b>Description:</b> {row.service_description}
              </Typography>
            </Grid>
          )}
          {row.next_due_at && (
            <Grid item xs={12}>
              <Typography variant="body2">
                <b>Next Due:</b> {row.next_due_at}
              </Typography>
            </Grid>
          )}
          {row.due_soon_at && (
            <Grid item xs={12}>
              <Typography variant="body2">
                <b>Due Soon:</b> {row.due_soon_at}
              </Typography>
            </Grid>
          )}
        </Grid>
      </CardContent>
    </Card>
  );

  const getReminders = (reminderTypesList: ReminderType[]) => {
    const payload = {
      actionId: 601,
      stateId: selectedState
    };

    FleetIoService.fleetIoPage(payload)
      .then((resp) => {
        const now = new Date();
        const warnings: Warning[] = [];
        const newCounts: CountsType = {};

        // Initialize counts for all reminder types
        reminderTypesList.forEach((type) => {
          newCounts[type.name] = { breached: 0, warning: 0 };
        });

        const transformedRows = resp.records.map((record: any) => {
          // Alert generation
          const dueSoonAt = record?.due_soon_at ? new Date(record.due_soon_at) : null;
          const nextDueAt = record?.next_due_at ? new Date(record.next_due_at) : null;

          const taskName = reminderTypesList.find((x) => x.id === record.vehicle_renewal_type_id)?.name ?? '--';
          let breached = false;
          let warning = false;

          if (nextDueAt && nextDueAt < now) {
            breached = true;
            const message = `${record.vehicle?.license_plate} ${taskName} is overdue by ${Math.abs(getDaysFromNow(record?.next_due_at))} days.`;
            warnings.push({ type: 'error', category: taskName, message });
            if (newCounts[taskName]) {
              newCounts[taskName].breached++;
            }
          } else if (dueSoonAt && dueSoonAt < now) {
            warning = true;
            const message = `${record.vehicle?.license_plate} ${taskName} due in ${getDaysFromNow(record?.next_due_at)} days.`;
            warnings.push({ type: 'warning', category: taskName, message });
            if (newCounts[taskName]) {
              newCounts[taskName].warning++;
            }
          }

          return {
            id: record.id,
            vehicle_name: record.vehicle?.name,
            license_plate: record.vehicle?.license_plate,
            make: record.vehicle?.make,
            model: record.vehicle?.model,
            renewal_type: taskName,
            next_due_at: formatDateToLongDate(record.next_due_at),
            due_soon_at: formatDateToLongDate(record.due_soon_at),
            status: record.vehicle_renewal_reminder_status,
            breached,
            warning,
            renewalType: taskName
          };
        });

        setCounts(newCounts);
        setRows(transformedRows);
        setWarningsData(warnings);
        hideBusyIndicator();
      })
      .catch((err: any) => {
        showAlert(formatErrorMessage(JSON.parse(err.response)));
        hideBusyIndicator();
        setRows([]);
        setWarningsData([]);
        setCounts({});
      });
  };

  const getReminderTypes = () => {
    const payload = {
      actionId: 700,
      stateId: selectedState
    };

    FleetIoService.fleetIoPage(payload)
      .then((resp: any) => {
        const reminderTypesList = resp?.records ?? [];
        setReminderTypes(reminderTypesList);
        getReminders(reminderTypesList);
      }).catch((err: any) => {
        setRows([]);
        setWarningsData([]);
        setReminderTypes([]);
        setCounts({});
        hideBusyIndicator();
        showAlert(formatErrorMessage(JSON.parse(err.response)));
      });
  };

  const refreshData = () => {
    getReminderTypes();
  };

  useEffect(() => {
    showBusyIndicator();
    clearAllAlerts();
    refreshData();
    EnumService.getEnums('states').then((states) => {
      setAllStates(states);
    }).catch();
  }, []);

  useEffect(() => {
    if (selectedState !== null) {
      showBusyIndicator();
      clearAllAlerts();
      refreshData();
    }
  }, [selectedState]);

  const onStatesSelect = (event: any) => {
    setSelectedState(event?.target?.value || 1);
  };

  return (
    <>
      <Helmet>
        <title>Renewal Reminders | {AppConstants.APP_NAME}</title>
      </Helmet>
      <Box
        sx={{
          backgroundColor: 'background.default',
          minHeight: '100%',
          py: 3
        }}
      >
        <Container maxWidth="xl">
          <Card>
            <CardHeader
              title="Renewal Reminders"
              action={
                (
                  <>
                    <IconButton onClick={refreshData}>
                      <RefreshIcon />
                    </IconButton>
                  </>
                )
              }
            />
            <Divider />
            <CardContent>
              <Toolbar style={{ justifyContent: 'flex-end' }}>
                <TextField
                  sx={{ width: '200px', marginRight: '1rem' }}
                  size="small"
                  label="State"
                  name="state"
                  onChange={onStatesSelect}
                  onBlur={onStatesSelect}
                  select
                  value={selectedState}
                >
                  {allStates.map((option) => (
                    <MenuItem key={option.id} value={option.id}>
                      <ListItemText primary={option.value} />
                    </MenuItem>
                  ))}
                </TextField>
              </Toolbar>
              <Box
                sx={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 2,
                  mb: 2,
                  marginTop: 2,
                  alignItems: 'center',
                  justifyContent: 'flex-start',
                }}
              >
                {reminderTypes.map((reminderType) => (
                  <React.Fragment key={reminderType.id}>
                    {/* Breached chip */}
                    <Stack direction="row" spacing={0.5} alignItems="center">
                      <Chip
                        icon={<ErrorIcon style={{ color: '#d32f2f' }} fontSize="small" />}
                        label={(
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="body2" component="span" sx={{ fontWeight: 600 }}>
                              {counts[reminderType.name]?.breached || 0}
                            </Typography>
                            <Typography
                              variant="body2"
                              component="span"
                              sx={{
                                ml: 0.5,
                                fontWeight: selectedSummary === `${reminderType.name} Breached` ? 700 : 400,
                                cursor: 'pointer',
                              }}
                            >
                              {reminderType.name}
                            </Typography>
                          </Box>
                        )}
                        clickable
                        onClick={() => setSelectedSummary(`${reminderType.name} Breached`)}
                        onDelete={selectedSummary === `${reminderType.name} Breached` ? () => setSelectedSummary('') : undefined}
                        deleteIcon={selectedSummary === `${reminderType.name} Breached` ? <CancelIcon fontSize="small" /> : undefined}
                        sx={{
                          height: 36,
                          borderRadius: 18,
                          border: 'none',
                          transition: 'all 0.2s ease',
                          fontWeight: selectedSummary === `${reminderType.name} Breached` ? 700 : 400,
                        }}
                        variant={selectedSummary === `${reminderType.name} Breached` ? 'filled' : 'outlined'}
                      />
                    </Stack>
                    {/* Warning chip */}
                    <Stack direction="row" spacing={0.5} alignItems="center">
                      <Chip
                        icon={<ReportProblemIcon style={{ color: '#ffa000' }} fontSize="small" />}
                        label={(
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="body2" component="span" sx={{ fontWeight: 600 }}>
                              {counts[reminderType.name]?.warning || 0}
                            </Typography>
                            <Typography
                              variant="body2"
                              component="span"
                              sx={{
                                ml: 0.5,
                                fontWeight: selectedSummary === `${reminderType.name} Warning` ? 700 : 400,
                                cursor: 'pointer',
                              }}
                            >
                              {reminderType.name}
                            </Typography>
                          </Box>
                        )}
                        clickable
                        onClick={() => setSelectedSummary(`${reminderType.name} Warning`)}
                        onDelete={selectedSummary === `${reminderType.name} Warning` ? () => setSelectedSummary('') : undefined}
                        deleteIcon={selectedSummary === `${reminderType.name} Warning` ? <CancelIcon fontSize="small" /> : undefined}
                        sx={{
                          height: 36,
                          borderRadius: 18,
                          border: 'none',
                          transition: 'all 0.2s ease',
                          fontWeight: selectedSummary === `${reminderType.name} Warning` ? 700 : 400,
                        }}
                        variant={selectedSummary === `${reminderType.name} Warning` ? 'filled' : 'outlined'}
                      />
                    </Stack>
                  </React.Fragment>
                ))}
              </Box>
              {/* Filter rows and warnings based on selectedSummary */}
              {(() => {
                let filteredRows = rows;
                let filteredWarnings = warningsData;

                if (selectedSummary) {
                  const isBreached = selectedSummary.endsWith(' Breached');
                  const isWarning = selectedSummary.endsWith(' Warning');
                  const reminderTypeName = selectedSummary.replace(/ (Breached|Warning)$/, '');

                  if (isBreached) {
                    filteredRows = rows.filter((r: any) => r.renewalType === reminderTypeName && r.breached);
                    filteredWarnings = warningsData.filter((w) => w.category === reminderTypeName && w.type === 'error');
                  } else if (isWarning) {
                    filteredRows = rows.filter((r: any) => r.renewalType === reminderTypeName && r.warning);
                    filteredWarnings = warningsData.filter((w) => w.category === reminderTypeName && w.type === 'warning');
                  }
                }

                const visibleFilteredWarnings = showAll ? filteredWarnings : filteredWarnings?.slice(0, 3);

                return (
                  <>
                    {/* <Box sx={{ mt: 2, mb: 4 }}>
                      {visibleFilteredWarnings?.map((alert) => (
                        <Alert sx={{ margin: 1 }} key={alert.message} severity={alert.type}>
                          {alert.message}
                        </Alert>
                      ))}

                      {filteredWarnings?.length > 3 && (
                        <Box sx={{ textAlign: 'right', mt: 1, mr: 2 }}>
                          <Typography
                            variant="body2"
                            sx={{ cursor: 'pointer', color: 'primary.main', textDecoration: 'underline' }}
                            onClick={() => setShowAll(!showAll)}
                          >
                            {showAll ? 'Show less' : `Show ${filteredWarnings.length - 3} more`}
                          </Typography>
                        </Box>
                      )}
                    </Box> */}
                    <Hidden mdDown>
                      <Table
                        rows={filteredRows}
                        columns={columns}
                        page={page}
                        getRowClassName={(params: any) => (params?.row.breached && 'error') || (params?.row.warning && 'warning') || ''}
                        totalRows={filteredRows.length}
                        sortModel={sortModel}
                        onPageChange={(newPage) => setPage(newPage)}
                        onSortModelChange={(model) => setSortModel(model)}
                      />
                    </Hidden>
                    <Hidden mdUp>
                      <MobileDataList
                        rows={filteredRows}
                        renderEachRow={renderEachMobileServiceCard}
                        totalRows={filteredRows.length}
                        page={page}
                        onPageChange={(newPage: any) => setPage(newPage)}
                      />
                    </Hidden>
                  </>
                );
              })()}
            </CardContent>
          </Card>
        </Container>
      </Box>
    </>
  );
};

const mapStateToProps = null;

const mapDispatchToProps = (dispatch: any) => ({
  showBusyIndicator: () => dispatch(show()),
  hideBusyIndicator: () => dispatch(hide()),
  showAlert: (message: any) => dispatch(showGlobalAlert(message)),
  clearAllAlerts: () => dispatch(clearAllGlobalAlerts())
});

export default connect(mapStateToProps, mapDispatchToProps)(RenewalReminders);
